import { useGoogleMap } from '@react-google-maps/api';
import React, { ReactElement, useEffect, useState } from 'react';
import { mapCoordinatesToLatLng } from '@hotelplan/components.common.map-pin';
import { useTransfer } from '@hotelplan/fdr.lib.router.push';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { FdrAgencyFragment } from 'schemas/fragment/agency/fdr-agency.generated';
import { FdrAgencyItemFragment } from 'schemas/fragment/recommenders/agency/fdr-agency-recommender.generated';
import { FdrAgencyPin } from './fdr-agency-pin';

interface IFdrAgencyMapPinsProps {
  agencies?: Array<FdrAgencyFragment>;
  selectedId: string;
  fitBounds?: boolean;
}

export function FdrAgencyMapPins({
  agencies,
  selectedId,
  fitBounds = false,
}: IFdrAgencyMapPinsProps): ReactElement {
  const map = useGoogleMap();
  const { transfer } = useTransfer();
  const { mobile } = useDeviceType();

  useEffect(() => {
    if (!fitBounds) return;
    if (!map || !agencies) return;

    const bounds = new google.maps.LatLngBounds();

    agencies.forEach(agency => {
      if (agency.coordinates) {
        bounds.extend(mapCoordinatesToLatLng(agency.coordinates));
      }
    });

    requestAnimationFrame(() => {
      map.fitBounds(bounds, 30);
    });
  }, [map, agencies, fitBounds]);

  useEffect(() => {
    if (!map || fitBounds || !selectedId) return;
    const selectedAgency = agencies?.find(agency => agency.id === selectedId);
    if (!selectedAgency) return;

    if (selectedAgency.coordinates) {
      requestAnimationFrame(() => {
        map.setCenter(mapCoordinatesToLatLng(selectedAgency.coordinates));
      });
    }
  }, [map, agencies, selectedId]);

  function onPinClick(agency: FdrAgencyItemFragment) {
    !mobile && agency?.webMeta?.link
      ? transfer(agency?.webMeta?.link, false, true)
      : undefined;
  }

  const [activeAgency, setActiveAgency] = useState<string>(undefined);

  if (!agencies) return null;

  return (
    <>
      {agencies.map(agency => {
        return (
          <FdrAgencyPin
            key={agency.id}
            agency={agency}
            onClick={onPinClick}
            selected={agency.id === selectedId}
            showDetailedInfo={activeAgency === agency.id}
            setActiveAgency={setActiveAgency}
          />
        );
      })}
    </>
  );
}
